import { useCallback, useId } from 'react';
import formStyles from '~/styles/form.module.css';
import numberFieldStyles from '~/styles/number-field.module.css';
import cnh from '~/helpers/cnh';
import useTranslation from '~/hooks/useTranslation';
import IconMinus from '~icons/tabler/minus';
import IconPlus from '~icons/tabler/plus';
import FormLabel from './FormLabel';

type Props = {
  label: string;
  value: string | number;
  required?: boolean;
  min?: number;
  max?: number;
  visuallyHiddenLabel?: boolean;
  onChange: (value: number) => void;
};

export default function PlainNumberField({
  label,
  value,
  required = false,
  min,
  max,
  visuallyHiddenLabel = false,
  onChange,
}: Props) {
  const fieldId = useId();
  const translate = useTranslation();

  const onDecrease = useCallback(() => {
    if (min && Number(value) <= min) {
      return;
    }

    onChange(Number(value) - 1);
  }, [value, min, onChange]);

  const onIncrease = useCallback(() => {
    if (max && Number(value) >= max) {
      return;
    }

    onChange(Number(value) + 1);
  }, [value, max, onChange]);

  return (
    <div>
      <FormLabel
        htmlFor={fieldId}
        visuallyHidden={visuallyHiddenLabel}
        required={required}
      >
        {label}
      </FormLabel>

      <div className={numberFieldStyles.numberField}>
        <button
          type="button"
          className={numberFieldStyles.numberFieldButton}
          onClick={onDecrease}
          disabled={value === min}
        >
          <IconMinus className={numberFieldStyles.numberFieldIcon} />
          <span className={numberFieldStyles.numberFieldButtonText}>
            {translate('form.decrease')}
          </span>
        </button>
        <input
          type="number"
          id={fieldId}
          className={cnh(formStyles.formControl, numberFieldStyles.numberFieldInput)}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          required={required}
          min={min}
          max={max}
        />
        <button
          type="button"
          className={numberFieldStyles.numberFieldButton}
          onClick={onIncrease}
          disabled={value === max}
        >
          <IconPlus className={numberFieldStyles.numberFieldIcon} />
          <span className={numberFieldStyles.numberFieldButtonText}>
            {translate('form.increase')}
          </span>
        </button>
      </div>
    </div>
  );
}
